import { faEnvelope, faPenNib } from '@fortawesome/pro-regular-svg-icons'
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons/faArrowUpRightFromSquare'
import { faBan } from '@fortawesome/pro-regular-svg-icons/faBan'
import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons/faCircleQuestion'
import { faDownload } from '@fortawesome/pro-regular-svg-icons/faDownload'
import { faRotateRight } from '@fortawesome/pro-regular-svg-icons/faRotateRight'
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons/faTrashAlt'
import { Flex, Loader, Button, Text, Switch, Tooltip, Box } from '@mantine/core'
import { useRouter } from 'next/router'
import { useRef, useState } from 'react'

import { useAnalytics } from '@flecto/analytics'
import { useQueryGetBookingById } from '@flecto/core/saas'
import { useMutationCreateDocumentTemplateFromPdf } from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import { type UNSAFE_any, type DocusealTemplateResponse, type CoreBookingDocument } from '@flecto/types'
import { Icon, useNotifications } from '@flecto/ui'
import useMediaQuery from '@flecto/ui/lib/utils/useMediaQuery'

import {
  FLECTO_LINK_PUBLIC_URL,
  FLECTO_SAAS_URL,
} from '../../../../../../apps/saas/lib/constants'
import { type BookingActionOption } from '../../../booking-actions'
import { useModalBookingSendEmail } from '../../../booking-actions/hooks/useModalBookingSendEmail'
import { DeleteButton } from './DeleteButton'
import classes from './styles.module.css'

type UploadDocumentCardActionsProps = {
  document: CoreBookingDocument
  loading: boolean
  error: boolean
  errorDescription?: {
    id: string
    message: string
  }
  visible_to_renter: boolean
  onView?: () => void
  onDelete?: () => void
  onChange?: (field: string, value: string | boolean) => void
  onCancel?: () => void
  onRetry?: () => void
  readonly?: boolean
  knownFileRejectionErrors?: string[]
  isFlectoLink?: boolean
}

export const UploadDocumentCardActions = ({
  document: documentFile,
  readonly = false,
  loading,
  error,
  visible_to_renter,
  onChange,
  onDelete,
  onView,
  onCancel,
  onRetry,
  errorDescription,
  knownFileRejectionErrors = [],
  isFlectoLink = false,
}: UploadDocumentCardActionsProps) => {
  const { isDesktop } = useMediaQuery()
  const { t } = useTranslation('saas', { keyPrefix: 'booking' })
  const { i18n } = useTranslation('shared', {
    keyPrefix: 'actions',
  })
  const { showNotification } = useNotifications()
  const [tooltipVisible, setTooltipVisible] = useState(false)
  const hasRedirected = useRef(false)
  const track = useAnalytics('segment')
  const router = useRouter()

  const { openModal: openModalSendEmail } = useModalBookingSendEmail()

  const bookingId = router?.query?.id as string

  const { data: booking } = useQueryGetBookingById({
    bookingId,
  })

  const {
    isLoading: isLoadingGenerateTemplate,
    mutateAsync: createTemplateOnDocuseal,
  } = useMutationCreateDocumentTemplateFromPdf()

  const handleTooltipClick = () => {
    setTooltipVisible((prev: UNSAFE_any) => !prev)
  }

  const handleTooltipMouseOver = () => {
    setTooltipVisible(true)
  }

  const handleTooltipMouseOut = () => {
    setTooltipVisible(false)
  }

  const handleDownload = async () => {
    const fileUrl = documentFile?.url ?? ''
    const response = await fetch(fileUrl)
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = Object.assign(document.createElement('a'), {
      href: url,
      download: documentFile?.filename || 'download',
      style: 'display: none',
    })

    document.body.appendChild(a)
    a.click()
    URL.revokeObjectURL(url)
    a.remove()
  }

  const isDocGenerated = Boolean(documentFile.generated)

  const isSignAvailable = Boolean(
    isDocGenerated &&
      ((documentFile.category === 'quote' &&
        documentFile?.snapshot?.booking_settings
          ?.documents_quote_include_signatures) ||
        (documentFile.category === 'contract' &&
          documentFile?.snapshot?.booking_settings
            ?.documents_contract_include_signatures))
  )

  const handleGenerateTemplate = async () => {
    if (!isSignAvailable) {
      return
    }

    try {
      const template = await createTemplateOnDocuseal({
        document: documentFile,
        external_id: String(documentFile.id ?? ''),
        application_key: isFlectoLink ? 'link' : 'saas',
      })

      redirectToSign(template)
    } catch {
      showNotification({
        type: 'error',
        message: 'Something went wrong. Please try again.',
      })
    }
  }

  const redirectToSign = (templateData: DocusealTemplateResponse) => {
    const baseParams = {
      c_email: isFlectoLink
        ? (documentFile?.snapshot?.customer?.email ?? '')
        : (booking?.customer?.email ?? ''),
      origin: isFlectoLink ? 'link' : 'saas',
      c_locale:isFlectoLink
        ? (documentFile?.snapshot?.customer?.locale ?? '')
        : (booking?.customer?.locale ?? ''),
      d_id: String(documentFile.id ?? ''),
      cta_label: documentFile.filename_base ?? '',
      c_name: documentFile?.snapshot?.owner?.name ?? '',
      d_name: documentFile.filename_base ?? '',
      is_preview: String(documentFile?.signed ?? false),
    }

    if (isFlectoLink) {
      const { slug } = router.query
      const path = `${FLECTO_LINK_PUBLIC_URL}/${slug}/sign/${templateData?.slug ?? ''}`

      const searchParams = new URLSearchParams({
        ...baseParams,
        cta_link: window.location.href,
      })

      const url = `${path}?${searchParams.toString()}`

      window.location.href = url
    } else {
      const ctaLink = `${FLECTO_SAAS_URL}/bookings/${String(booking?.internal_id ?? '')}`

      const targetUrl = new URL(
        `${booking?.permalink ?? ''}/sign/${templateData?.slug ?? ''}`,
        FLECTO_LINK_PUBLIC_URL
      )

      Object.entries({ ...baseParams, cta_link: ctaLink }).forEach(
        ([key, value]) => {
          targetUrl.searchParams.append(key, value)
        }
      )

      window.open(targetUrl.href, '_blank')
    }

    hasRedirected.current = true
  }

  if (error) {
    const isKnownFileRejection = knownFileRejectionErrors.includes(
      errorDescription?.id ?? ''
    )

    return (
      !isKnownFileRejection && (
        <Flex gap={'sm'}>
          <Button
            size='sm'
            variant='outline'
            color='red'
            leftSection={<Icon icon={faRotateRight} />}
            data-testid={'upload-btn-try-again'}
            onClick={onRetry}
          >
            {t('booking_documents.try_again')}
          </Button>
          <Button
            size='sm'
            variant='default'
            color='gray'
            leftSection={<Icon icon={faTrashAlt} />}
            data-testid={'upload-btn-delete'}
            onClick={onDelete}
          >
            {t('booking_documents.delete')}
          </Button>
        </Flex>
      )
    )
  }

  return (
    <Flex gap={!isDesktop ? 'lg' : 'sm'} wrap={'wrap'}>
      {loading && !error ? (
        <Flex
          gap={'sm'}
          align={'center'}
          {...(!isDesktop && { gap: 'lg', direction: 'column' })}
        >
          <Loader size='sm' />
          <Button
            size='sm'
            variant='default'
            color='gray'
            leftSection={<Icon icon={faBan} />}
            data-testid={'upload-btn-cancel'}
            onClick={onCancel}
          >
            {t('booking_documents.cancel')}
          </Button>
        </Flex>
      ) : (
        <>
          {!readonly && (
            <Flex align={'center'} gap={'sm'} mr={'sm'}>
              <Switch
                checked={visible_to_renter}
                data-testid={'upload-switch-show-on-flecto'}
                onChange={(event) =>
                  onChange?.('visible_to_renter', event.currentTarget.checked)
                }
              />

              <Text size={'md'} fw={500} c='gray.9'>
                {t('booking_documents.show_on_flecto')}
              </Text>
              <span
                onClick={handleTooltipClick}
                onMouseOver={handleTooltipMouseOver}
                onMouseOut={handleTooltipMouseOut}
              >
                <Tooltip
                  label={t('booking_documents.show_on_flecto_tooltip')}
                  multiline
                  zIndex={11000}
                  withinPortal
                  onClick={handleTooltipClick}
                  opened={tooltipVisible}
                >
                  <span>
                    <Icon
                      icon={faCircleQuestion}
                      size='xs'
                      color='gray'
                      mr='sm'
                    />
                  </span>
                </Tooltip>
              </span>
            </Flex>
          )}
          <Button.Group>
            <Tooltip
              label={t('booking_documents.tooltip_button_open')}
              multiline
              zIndex={11000}
              withinPortal
            >
              <Button
                variant='default'
                size='sm'
                onClick={onView}
                data-testid={'upload-btn-view'}
              >
                <Icon icon={faArrowUpRightFromSquare} />
              </Button>
            </Tooltip>

            {isSignAvailable && !isFlectoLink && (
              <Tooltip
                multiline
                withinPortal
                zIndex={11000}
                label={t('booking_documents.tooltip_button_sign')}
              >
                <Button
                  size='sm'
                  variant='default'
                  data-testid={'sign-btn'}
                  loading={isLoadingGenerateTemplate}
                  onClick={handleGenerateTemplate}
                >
                  <Icon icon={faPenNib} />
                </Button>
              </Tooltip>
            )}

            {!isFlectoLink && (
              <Tooltip
                label={t('booking_documents.tooltip_button_send_email')}
                multiline
                zIndex={11000}
                withinPortal
              >
                <Button
                  variant='default'
                  size='sm'
                  data-testid={'send-email-btn-view'}
                  onClick={(e) => {
                    e.preventDefault()
                    openModalSendEmail({
                      template: {
                        label: '',
                        value: documentFile.category,
                      } as BookingActionOption,
                      document: documentFile,
                    })
                  }}
                >
                  <Icon icon={faEnvelope} />
                </Button>
              </Tooltip>
            )}

            {isFlectoLink && (
              <Tooltip
                label={t('booking_documents.tooltip_button_download')}
                multiline
                zIndex={11000}
                withinPortal
              >
                <Button
                  variant='default'
                  color='gray'
                  size='sm'
                  onClick={handleDownload}
                  data-testid={'download-btn-view'}
                >
                  <Icon icon={faDownload} />
                </Button>
              </Tooltip>
            )}
            {!readonly && (
              <Tooltip
                label={t('booking_documents.tooltip_button_delete')}
                multiline
                zIndex={11000}
                withinPortal
              >
                <Box className={classes.unstyledButton}>
                  <DeleteButton document={documentFile} onDelete={onDelete} />
                </Box>
              </Tooltip>
            )}
          </Button.Group>
          {isFlectoLink && isSignAvailable && (
            <Button
              ml='sm'
              variant={documentFile?.signed ? 'outline' : 'filled'}
              color='brand'
              size='sm'
              loading={isLoadingGenerateTemplate}
              data-testid={'sign-button'}
              onClick={() => {
                track('BOOKING_DOCUMENT__SIGN__NEW', {
                  bookingId: documentFile?.snapshot?.booking.id ?? '',
                  category: documentFile.category ?? '',
                  document_id: documentFile.id ?? '',
                  file_size: documentFile?.byte_size ?? 0,
                  file_type: documentFile?.content_type ?? '',
                  filename: documentFile.filename_base ?? '',
                  generated: documentFile.generated ?? false,
                  id: documentFile.id ?? '',
                  visible_to_renter: documentFile.visible_to_renter ?? false,
                  uploaded_at: documentFile.created_at ?? '',
                  source: 'booking_link',
                })

                handleGenerateTemplate()
              }}
              leftSection={<Icon icon={faPenNib} />}
            >
              {t('booking_documents.tooltip_button_sign')}
            </Button>
          )}
        </>
      )}
    </Flex>
  )
}
